import DeleteAccountButton from '../../components/DeleteAccountButton';
import React, { useState, useEffect, useRef } from 'react';
import ConversationActions from '../../components/ConversationActions';
import { Target, MessageCircle, BookOpen, Send, Zap, Wind, Eye, BarChart3, Share2 } from 'lucide-react';
import ResponseCounter from '../../components/auth/ResponseCounter';
import PaywallModal from '../../components/auth/PaywallModal';
import UserGuideModal from '../../components/dashboard/modals/UserGuideModal';
import BibleReferenceModal from '../../components/dashboard/modals/BibleReferenceModal';
import BiblicalTopicsModal from '../../components/dashboard/modals/BiblicalTopicsModal';
import ChangePasswordModal from '../../components/dashboard/modals/ChangePasswordModal';
import ShareModal from '../../components/dashboard/modals/ShareModal';
import PracticeDetailModal from '../../components/dashboard/modals/PracticeDetailModal';
import ProgressTab from '../../components/dashboard/tabs/ProgressTab';
import AnalyticsTab from '../../components/dashboard/tabs/AnalyticsTab';
import conversationService from '../../services/conversations';
import ChatTab from '../../components/dashboard/tabs/ChatTab';
import SermonBuilder from '../../components/SermonBuilder';
import VoiceChoiceModal from '../../components/dashboard/modals/VoiceChoiceModal';
import { prepareCoachVoice, loadCoachVoices, saveCoachVoice, previewCoachVoice, playCoachVoice, stopCoachVoice, coachVoiceActive } from '../../utils/coachVoice';
import PracticeTab from '../../components/dashboard/tabs/PracticeTab';

// ============================================================================
// CONSCIOUSNESS LEVELS CONFIGURATION
// ============================================================================
const LEVELS = [
  { level: 1, name: "SEEKING", min: 0, max: 120, color: "#4A5568", badge: "Truth Seeker" },
  { level: 2, name: "UNDERSTANDING", min: 121, max: 300, color: "#4A5568", badge: "Power Seeker" },
  { level: 3, name: "EMBRACING", min: 301, max: 600, color: "#CD7F32", badge: "Love-Spectrum Activator" },
  { level: 4, name: "DEMONSTRATING", min: 601, max: 1000, color: "#FFD700", badge: "Clarity Bringer" },
  { level: 5, name: "SIMINATING", min: 1001, max: 1500, color: "#50C878", badge: "Fho Practitioner" },
  { level: 6, name: "TRANSFORMING", min: 1501, max: 2000, color: "#50C878", badge: "Energy Transformer" },
  { level: 7, name: "ASCENDING", min: 2001, max: 2700, color: "#0066FF", badge: "Swamp Climber" },
  { level: 8, name: "TRANSCENDING", min: 2701, max: 3500, color: "#0066FF", badge: "3d Swamp Escapee" },
  { level: 9, name: "ILLUMINATING", min: 3501, max: 4500, color: "#9966CC", badge: "I-WE Harmonizer" },
  { level: 10, name: "EMBODYING", min: 4501, max: 5800, color: "#9966CC", badge: "Free Happy One" },
  { level: 11, name: "RADIATING", min: 5801, max: 7500, color: "#B9F2FF", badge: "Spirit Made Physical" },
  { level: 12, name: "DIVINE STASIS", min: 7501, max: 999999, color: "#FFD700", badge: "Divine Stasis Master" }
];

function getLevelInfo(points) {
  const currentLevel = LEVELS.find(l => points >= l.min && points <= l.max) || LEVELS[0];
  const nextLevel = LEVELS[currentLevel.level] || currentLevel;
  const progressInLevel = points - currentLevel.min;
  const levelRange = currentLevel.max - currentLevel.min;
  const percentComplete = Math.min(100, (progressInLevel / levelRange) * 100);
  const pointsToNext = nextLevel.min - points;
  
  return {
    currentLevel,
    nextLevel,
    percentComplete: Math.round(percentComplete),
    pointsToNext: Math.max(0, pointsToNext),
    isMaxLevel: currentLevel.level === 12
  };
}

function getNextLevelUnlock(level) {
  const unlocks = {
    2: "Power MindStream + Imagination & Reasoning tools",
    3: "Flow MindStream + Intuition & Perception tools",
    4: "Clarity MindStream + All 36 Dimensions accessible",
    5: "Simination Score + FAME tracking + All tools active",
    6: "THAT Energy System + Freedom Score tracking",
    7: "3d Swamp Escape metrics + Rising from dependency",
    8: "Full Transcendence",
    9: "Fholific Leadership + Teaching capabilities",
    10: "Free Happy One status + Perfect Simination",
    11: "Spirit Made Physical + Legacy creation",
    12: "Divine Stasis Master + Co-creator status"
  };
  return unlocks[level] || "Advanced features";
}

const MinimalDashboard = () => {
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  const apiUrl = window.location.origin;
  const [activeTab, setActiveTab] = useState('chat');
  // Research mode: 'select' = mode picker, 'research' = explore mode, 'paper' = writing mode
  const [researchMode, setResearchMode] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('mode') === 'sermon' ? 'sermon' : 'select'; } catch (e) { return 'select'; }
  });
  
  // Share modal states
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [fhoAppsOpen, setFhoAppsOpen] = useState(false);
  const menuRef = React.useRef(null);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);
  
  // Data states
  const [userData, setUserData] = useState(null);
  const [userName, setUserName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Chat states
  const [userInput, setUserInput] = useState('');
  const [detectedUrl, setDetectedUrl] = useState('');
  const [uploadedDoc, setUploadedDoc] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const _nm = file.name.toLowerCase(); if (!_nm.endsWith('.pdf') && !_nm.endsWith('.docx')) { setChatError('Only PDF and Word (.docx) files are supported.'); e.target.value=''; return; }
    setIsUploading(true); setChatError('');
    try {
      const form = new FormData(); form.append('file', file);
      const res = await fetch(`${apiUrl}/api/v1/emit-research/upload`, { method: 'POST', body: form });
      if (!res.ok) { const err = await res.json().catch(()=>({})); setChatError(err.detail || 'Upload failed.'); setIsUploading(false); e.target.value=''; return; }
      const data = await res.json();
      setUploadedDoc(data.content); setUploadedFileName(data.filename);
    } catch (err) { setChatError('Upload failed. Please try again.'); }
    setIsUploading(false); e.target.value='';
  };
  const [isProcessing, setIsProcessing] = useState(false);
  const [chatError, setChatError] = useState('');
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [queryCategory, setQueryCategory] = useState('biblical');
  
  // Practice states
  const [practiceForm, setPracticeForm] = useState({
    mindstream_type: 'power',
    practice_type: '',
    practice_name: '',
    duration_minutes: 30,
    quality_rating: 8,
    insights_gained: ''
  });
  const [isSubmittingPractice, setIsSubmittingPractice] = useState(false);
  const [practiceSuccess, setPracticeSuccess] = useState('');
  const [practiceError, setPracticeError] = useState('');

  // Practice detail modal states
  const [selectedPractice, setSelectedPractice] = useState(null);

  // Free Trial State
  const [showPaywall, setShowPaywall] = useState(false);
  const [responsesRemaining, setResponsesRemaining] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showPracticeModal, setShowPracticeModal] = useState(false);

  // Bible reference modal state
  const [showBibleReference, setShowBibleReference] = useState(false);
  const [showBiblicalTopics, setShowBiblicalTopics] = useState(false);
  const [showConversationHistory, setShowConversationHistory] = useState(false);
  const [researchConversations, setResearchConversations] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showUserGuide, setShowUserGuide] = useState(false);

  // Change password modal states
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const messagesEndRef = useRef(null);
  const latestMessageRef = useRef(null);

  // ============================================
  // VOICE FUNCTIONALITY HOOK
  // ============================================
  const useVoiceChat = () => {
    const [isListening, setIsListening] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [voiceEnabled, setVoiceEnabled] = useState(
      typeof window !== 'undefined' && localStorage.getItem('emitVoiceEnabled') === 'true'
    );
    
    const recognitionRef = useRef(null);
    const synthRef = useRef(typeof window !== 'undefined' ? window.speechSynthesis : null);

    useEffect(() => {
      if (typeof window === 'undefined') return;
      
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      
      if (SpeechRecognition) {
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = false;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = 'en-US';

        recognitionRef.current.onresult = (event) => {
          const current = event.resultIndex;
          const transcriptText = event.results[current][0].transcript;
          setTranscript(transcriptText);
        };

        recognitionRef.current.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
        };

        recognitionRef.current.onend = () => {
          setIsListening(false);
        };
      }

      return () => {
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
        if (synthRef.current) {
          synthRef.current.cancel();
        }
      };
    }, []);

    const startListening = () => {
      if (recognitionRef.current && !isListening) {
        setTranscript('');
        recognitionRef.current.start();
        setIsListening(true);
      }
    };

    const stopListening = () => {
      if (recognitionRef.current && isListening) {
        recognitionRef.current.stop();
        setIsListening(false);
      }
    };

    // Strip markdown for voice - removes links, bold, italic, underline
    const stripMarkdownForVoice = (text) => {
      return text
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1')
        .replace(/__([^_]+)__/g, '$1')
        .replace(/_([^_]+)_/g, '$1')
        .replace(/FHO-OS/gi, 'foe OS')
        .replace(/FHO/gi, 'foe');
    };

    // EMIT_VOICE_V1: short replies use the chosen ElevenLabs voice; the browser voice is the fallback
    const speak = (text) => {
      if (!voiceEnabled) return;
      if (coachVoiceActive()) {
        playCoachVoice(text, () => setIsSpeaking(true), () => setIsSpeaking(false), (rest) => speakDevice(rest));
        return;
      }
      speakDevice(text);
    };
    const speakDevice = (text) => {
      if (!voiceEnabled || !synthRef.current) return;
      synthRef.current.cancel();
      
      const cleanText = stripMarkdownForVoice(text);
      const utterance = new SpeechSynthesisUtterance(cleanText);
      utterance.rate = 0.90;
      utterance.pitch = 0.85;
      utterance.volume = 1.0;
      
      const voices = synthRef.current.getVoices();
      
      // Try to find any English male voice
      const preferredVoice = voices.find(v => 
        v.lang.startsWith('en') && v.name.toLowerCase().includes('male')
      );
      
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }

      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);

      synthRef.current.speak(utterance);
    };

    const stopSpeaking = () => {
      stopCoachVoice();
      if (synthRef.current) {
        synthRef.current.cancel();
        setIsSpeaking(false);
      }
    };

    const toggleVoice = () => {
      const newState = !voiceEnabled;
      setVoiceEnabled(newState);
      if (typeof window !== 'undefined') {
        localStorage.setItem('emitVoiceEnabled', newState);
      }
      if (!newState) {
        stopSpeaking();
      }
    };

    return {
      isListening,
      isSpeaking,
      transcript,
      voiceEnabled,
      startListening,
      stopListening,
      speak,
      stopSpeaking,
      toggleVoice,
      isSupported: !!recognitionRef.current
    };
  };

  const voice = useVoiceChat();
  const lastSpokenMessageRef = useRef(null);
  // EMIT_VOICE_V1: voice choice (same voices as the other FHO apps)
  const [showVoicePanel, setShowVoicePanel] = useState(false);
  const [voiceList, setVoiceList] = useState([]);
  const [voiceChoice, setVoiceChoice] = useState(null);
  useEffect(() => {
    prepareCoachVoice();
    loadCoachVoices().then((d) => { if (d) { setVoiceList(d.voices); setVoiceChoice(d.choice); } });
    return () => { stopCoachVoice(); };
  }, []);

  // Categories definition
  const categories = [
    { 
      value: 'biblical', 
      label: '📖 Biblical Study', 
      icon: '📖',
      description: 'Scripture analysis through Fhoism lens (9-section format)'
    },
    { 
      value: 'general', 
      label: '💬 General Guidance', 
      icon: '💬',
      description: 'Open dialogue about any topic'
    },
    { 
      value: 'self-development', 
      label: '🌱 Self-Development', 
      icon: '🌱',
      description: 'Personal growth & transformation steps'
    },
    { 
      value: 'mindstreams', 
      label: '🧠 MindStreams', 
      icon: '🧠',
      description: 'Power, Flow & Clarity practices'
    }
  ];

  // ============================================
  // DATA FETCHING
  // ============================================
  useEffect(() => {
    fetchDashboardData();
    loadConversations();
  }, []);
  // Check for EMIT Research profile - redirect to onboarding if not set up
  useEffect(() => {
    const checkResearchProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await fetch(`${apiUrl}/api/v1/emit-research/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          // If no research profile exists, redirect to setup
          if (!data.has_profile) {
            window.location.href = '/emit-research/setup';
          }
        }
      } catch (err) {
        console.log('Research profile check error:', err);
      }
    };
    checkResearchProfile();
  }, []);

  // Update input when voice transcript changes
  useEffect(() => {
    if (voice.transcript) {
      setUserInput(voice.transcript);
    }
  }, [voice.transcript]);

  // Detect URLs in Research Mode input
  useEffect(() => {
    if (researchMode === 'research') {
      const urlMatch = userInput.match(/https?:\/\/[^\s]+/);
      setDetectedUrl(urlMatch ? urlMatch[0] : '');
    } else {
      setDetectedUrl('');
    }
  }, [userInput, researchMode]);

  // Auto-send when browser silence detection cuts mic
  const wasListeningRef = useRef(false);
  useEffect(() => {
    if (wasListeningRef.current && !voice.isListening && userInput.trim()) {
      setTimeout(() => handleSubmit(), 150);
    }
    wasListeningRef.current = voice.isListening;
  }, [voice.isListening]);

  // Cross-App Integration: Handle URL params for deep linking from other FHO apps
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const promptParam = urlParams.get('prompt');
    const autoSend = urlParams.get('send') === 'true';
    
    if (promptParam) {
      const decodedPrompt = decodeURIComponent(promptParam);
      setUserInput(decodedPrompt);
      
      // Clear URL params after reading (clean URL)
      window.history.replaceState({}, '', window.location.pathname);
      
      // Auto-send if requested
      if (autoSend) {
        setTimeout(() => {
          handleSubmit(decodedPrompt);
        }, 500);
      }
    }
  }, []);
  
  // Journey Focus: Check for prompt from Journey Dashboard and auto-send
  useEffect(() => {
    const journeyPrompt = localStorage.getItem('journeyFocusPrompt');
    if (journeyPrompt) {
      // Clear immediately to prevent re-triggering
      localStorage.removeItem('journeyFocusPrompt');
      localStorage.removeItem('journeyFocusCategory');
      
      // Set input and auto-send after short delay
      setUserInput(journeyPrompt);
      setTimeout(() => {
        handleSubmit(journeyPrompt);
      }, 500);
    }
  }, []);

  // Scroll to top when switching to practice tab
  useEffect(() => {
    if (activeTab === "practice") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [activeTab]);

  // Load research conversation history
  const loadResearchHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(apiUrl + '/api/v1/emit-research/conversations', {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (response.ok) {
        const data = await response.json();
        setResearchConversations(data.conversations || []);
      }
    } catch (err) {
      console.error('Error loading research history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load a specific conversation
  // Per-reply Word download (Sep 29 2026): sends the reply text, not an index,
  // so unsaved lines in the chat (errors, welcome text) can't shift which reply exports.
  const downloadReply = async (msg) => {
    try {
      const conv = conversations.find(c => c.id === currentConversationId) || conversations[conversations.length - 1];
      const list = conv ? conv.messages : [];
      const at = list.findIndex(m => m.id === msg.id);
      const question = list.slice(0, at < 0 ? list.length : at).reverse().find(m => m.role === 'user');
      const res = await fetch(apiUrl + '/api/v1/emit-research/export-reply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('token') },
        body: JSON.stringify({ question: question ? question.content : '', content: msg.content })
      });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const match = (res.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = match ? match[1] : 'EMIT-Research-reply.docx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (err) {
      alert('Could not prepare the download. Please try again.');
    }
  };

  const loadConversationMessages = async (convId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(apiUrl + '/api/v1/emit-research/conversations/' + convId, {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (response.ok) {
        const data = await response.json();
        const conv = {
          id: convId,
          messages: data.messages.map((msg, idx) => ({
            id: convId + '_' + idx,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.timestamp).getTime()
          }))
        };
        setConversations(prev => {
          const exists = prev.find(c => c.id === convId);
          if (exists) {
            return prev.map(c => c.id === convId ? conv : c);
          }
          return [...prev, conv];
        });
        setCurrentConversationId(convId);
        setShowConversationHistory(false);
      }
    } catch (err) {
      console.error('Error loading conversation:', err);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Get the actual logged-in user's ID
      const storedUser = localStorage.getItem('user');
      const userId = storedUser ? JSON.parse(storedUser).id : 'test_user_123';
      
      const response = await fetch(`${apiUrl}/fho/dashboard/${userId}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();

      // Store subscription data
      setSubscriptionStatus(data.subscription_status);
      if (data.subscription_status === 'free') {
        setResponsesRemaining(data.responses_remaining);
        console.log('💳 Free trial user:', data.responses_remaining, 'responses remaining');
      } else {
        console.log('✅ Practitioner user: unlimited access');
      }
      
      // Transform data to match the format expected by the UI
      const transformedData = {
        profile: {
          username: data.profile?.username || 'User',
          email: data.profile?.email || 'user@emit.com',
          consciousness_points: data.profile?.consciousness_points || ((data.mindstreams || []).reduce((sum, s) => sum + (s.sessions_completed || 0), 0) * 28)
        },
        ecc_alert: data.ecc_alert || null,
        current_stage: {
          stage_number: data.profile?.consciousness_level || 1,
          stage_name: data.profile?.journey_stage || 'Beginning Journey'
        },
        progress: {
          total_practice_sessions: data.profile?.total_practices || (data.mindstreams || []).reduce((sum, s) => sum + (s.sessions_completed || 0), 0),
          days_active: data.daily_stats?.total_days_active || 0,
          streak_days: data.profile?.current_streak || Math.max(...(data.mindstreams || []).map(s => s.current_streak || 0), 0)
        },
        mindstreams: (data.mindstreams || []).map(stream => ({
          name: stream.stream_type === 'power' ? 'Power' : 
                stream.stream_type === 'flow' ? 'Flow' : 'Clarity',
          type: stream.stream_type,
          level: stream.level,
          score: Math.round(stream.progress_percentage || 0),
          sessions: stream.sessions_completed || 0,
          time_minutes: stream.total_time_minutes || 0,
          streak: stream.current_streak || 0
        })),
        dimensions: (data.das_abilities || []).map(ability => ({
          name: ability.ability_name,
          power: ability.power_name,
          mindstream: ability.das_type,
          score: Math.round(ability.mastery_percentage || 0),
          times_practiced: ability.times_practiced || 0,
          is_active: ability.is_active
        })),
        recent_practices: (data.recent_practices || []).length > 0 
          ? data.recent_practices.map(practice => ({
              practice_type: practice.practice_name,
              notes: practice.insights_gained || 'No notes',
              duration_minutes: practice.duration_minutes,
              impact_score: practice.quality_rating,
              practice_date: practice.practiced_at,
              mindstream_type: practice.mindstream_type
            }))
          : data.mindstreams
              .filter(s => s.last_session)
              .map(stream => ({
                practice_type: `${stream.stream_type.charAt(0).toUpperCase() + stream.stream_type.slice(1)} Practice`,
                notes: `${stream.sessions_completed} sessions completed`,
                duration_minutes: Math.round(stream.total_time_minutes / Math.max(stream.sessions_completed, 1)),
                impact_score: Math.round(stream.average_session_quality || 0),
                practice_date: stream.last_session,
                mindstream_type: stream.stream_type
              }))
      };
      
      setUserData(transformedData);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadConversations = async () => {
    try {
      const loadedConversations = await conversationService.getConversations();
      setConversations(Array.isArray(loadedConversations) ? loadedConversations : []);
    } catch (err) {
      console.error('Error loading conversations:', err);
      setConversations([]);
    }
  };

  // ============================================
  // HELPER FUNCTIONS
  // ============================================
  const generateId = () => `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const generateTitleFromMessage = (message) => {
    const words = message.split(' ').slice(0, 5).join(' ');
    return words.length < message.length ? `${words}...` : words;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToLatestMessage = () => {
    latestMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const getMindStreamIcon = (type) => {
    switch(type) {
      case 'power': return <Zap className="mindstream-icon" style={{ color: '#0d6efd' }} />;
      case 'flow': return <Wind className="mindstream-icon" style={{ color: '#dc3545' }} />;
      case 'clarity': return <Eye className="mindstream-icon" style={{ color: '#198754' }} />;
      default: return <Target className="mindstream-icon" />;
    }
  };

  const getMindStreamColor = (type) => {
    switch(type) {
      case 'power': return '#0d6efd';
      case 'flow': return '#dc3545';
      case 'clarity': return '#198754';
      default: return '#4A90E2';
    }
  };


  // ============================================
  // CHAT HANDLERS - WITH STREAMING SUPPORT
  // ============================================
  const handleTopicSelect = (topicPrompt) => {
    setUserInput(topicPrompt);
    handleSubmit(topicPrompt);
  };

  const handleSubmit = async (directPrompt = null) => {
    const messageToSend = directPrompt || userInput;
    if (isProcessing || !messageToSend.trim()) {
      setChatError('Please enter a question or topic');
      return;
    }

    setIsProcessing(true);
    setChatError('');

    let activeConvId = currentConversationId;

    // Create new conversation if needed
    if (!activeConvId) {
      const newConv = {
        id: generateId(),
        title: generateTitleFromMessage(messageToSend),
        messages: [],
        category: queryCategory,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      const newConversations = [...conversations, newConv];
      setConversations(newConversations);
      setCurrentConversationId(newConv.id);
      activeConvId = newConv.id;
    }

    const userMessage = {
      id: generateId(),
      role: 'user',
      content: messageToSend,
      category: queryCategory,
      timestamp: Date.now()
    };

    try {
      // Add user message immediately
      setConversations(prevConversations => {
        const updated = prevConversations.map(conv => {
          if (conv.id === activeConvId) {
            return {
              ...conv,
              messages: [...conv.messages, userMessage],
              updatedAt: Date.now()
            };
          }
          return conv;
        });
        return updated;
      });

      setUserInput('');
      setTimeout(scrollToBottom, 100);

      const currentConv = Array.isArray(conversations) ? conversations.find(c => c.id === activeConvId) : null;
      const messageHistory = currentConv ? [...currentConv.messages, userMessage].slice(-10) : [userMessage];

      // ============================================================================
      // STREAMING REQUEST - NEW
      // ============================================================================
      const response = await fetch(`${apiUrl}/api/v1/emit-research`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt: userMessage.content,
          category: queryCategory,
          conversation_id: activeConvId,
          message_history: messageHistory,
          user_email: JSON.parse(localStorage.getItem("user"))?.email, // Add user email for free trial tracking
          stream: true,  // ✨ Enable streaming
          research_mode: researchMode === 'select' ? 'paper' : researchMode,
          url: researchMode === 'research' && detectedUrl ? detectedUrl : undefined,
          uploaded_document: uploadedDoc || undefined,
          uploaded_filename: uploadedFileName || undefined
        })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      // Create assistant message placeholder
      const assistantMessageId = generateId();
      let accumulatedText = '';

      // Add empty assistant message to show it's typing
      setConversations(prevConversations => {
        const updated = prevConversations.map(conv => {
          if (conv.id === activeConvId) {
            return {
              ...conv,
              messages: [...conv.messages, {
                id: assistantMessageId,
                role: 'assistant',
                content: '',
                category: queryCategory,
                timestamp: Date.now(),
                isStreaming: true
              }],
              updatedAt: Date.now()
            };
          }
          return conv;
        });
        return updated;
      });

      // Read the streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.error) {
                throw new Error(data.error);
              }

              if (data.chunk) {
                accumulatedText += data.chunk;

                // Update message with accumulated text
                setConversations(prevConversations => {
                  const updated = prevConversations.map(conv => {
                    if (conv.id === activeConvId) {
                      return {
                        ...conv,
                        messages: conv.messages.map(msg =>
                          msg.id === assistantMessageId
                            ? { ...msg, content: accumulatedText, isStreaming: !data.done }
                            : msg
                        ),
                        updatedAt: Date.now()
                      };
                    }
                    return conv;
                  });
                  return updated;
                });

                setTimeout(scrollToLatestMessage, 10);
              }

              // When streaming is done, trigger voice
              if (data.done) {
                // NEW: Update counter if provided
                if (data.responses_remaining !== undefined) {
                  console.log('📉 Responses remaining:', data.responses_remaining);
                  setResponsesRemaining(data.responses_remaining);
                  
                  // Show paywall if exhausted
                  if (data.responses_remaining === 0) {
                    setTimeout(() => setShowPaywall(true), 2000); // 60 second delay
                  }
                }
                // Mark streaming as complete
                setConversations(prevConversations => {
                  const updated = prevConversations.map(conv => {
                    if (conv.id === activeConvId) {
                      return {
                        ...conv,
                        messages: conv.messages.map(msg =>
                          msg.id === assistantMessageId
                            ? { ...msg, isStreaming: false }
                            : msg
                        ),
                        updatedAt: Date.now()
                      };
                    }
                    return conv;
                  });

                  return updated;
                });

                // Save conversation AFTER state update (non-blocking)
                setConversations(prevConversations => {
                  const updatedConv = prevConversations.find(c => c.id === activeConvId);
                  if (updatedConv) {
                    conversationService.updateConversation(activeConvId, {
                      title: updatedConv.title,
                      messages: updatedConv.messages,
                      updated_at: new Date(updatedConv.updatedAt).toISOString()
                    }).catch(err => console.warn('Conversation save failed (non-critical):', err.message));
                  }
                  return prevConversations; // Don't modify state
                });

                // ✨ TRIGGER VOICE AFTER COMPLETE RESPONSE
                if (voice.voiceEnabled && accumulatedText && accumulatedText.length <= 1500) {
                  setTimeout(() => {
                    voice.speak(accumulatedText);
                    lastSpokenMessageRef.current = assistantMessageId;
                  }, 500);  // Small delay to let UI settle
                }

                setTimeout(scrollToLatestMessage, 100);
              }
            } catch (parseError) {
              console.error('Error parsing SSE data:', parseError);
            }
          }
        }
      }

    } catch (err) {
      console.error('Error sending message:', err);
      setChatError('Failed to send message. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // ============================================
  // PRACTICE LOGGER HANDLERS
  // ============================================
  const handlePracticeSubmit = async (e) => {
    e.preventDefault();
    
    if (!practiceForm.practice_name.trim()) {
      setPracticeError('Please enter a practice name');
      return;
    }

    setIsSubmittingPractice(true);
    setPracticeError('');
    setPracticeSuccess('');

    try {
      // Get the actual logged-in user's ID
      const storedUser = localStorage.getItem('user');
      const userId = storedUser ? JSON.parse(storedUser).id : 'test_user_123';
      
      const response = await fetch(`${apiUrl}/fho/practices/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          mindstream_type: practiceForm.mindstream_type,
          practice_type: practiceForm.practice_type,
          practice_name: practiceForm.practice_name,
          duration_minutes: parseInt(practiceForm.duration_minutes),
          quality_rating: parseInt(practiceForm.quality_rating),
          insights_gained: practiceForm.insights_gained
        })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      if (data) {
        setPracticeSuccess('Practice logged successfully! 🎉');
        await fetchDashboardData(); // Refresh dashboard after practice
        setPracticeForm({
          mindstream_type: 'power',
          practice_type: '',
          practice_name: '',
          duration_minutes: 30,
          quality_rating: 8,
          insights_gained: ''
        });
        
        await fetchDashboardData();
        setTimeout(() => setPracticeSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error logging practice:', err);
      setPracticeError('Failed to log practice. Please try again.');
    } finally {
      setIsSubmittingPractice(false);
    }
  };

  // ============================================
  // PRACTICE MODAL HANDLERS
  // ============================================
  const handlePracticeClick = (practice) => {
    setSelectedPractice(practice);
    setShowPracticeModal(true);
  };


  // ============================================
  // CHANGE PASSWORD HANDLER
  // ============================================
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (passwordForm.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setIsChangingPassword(true);

    try {
      // Get user_id from stored user object
      const storedUser = localStorage.getItem('user');
      const userId = storedUser ? JSON.parse(storedUser).id : 'test_user_123';
      
      const response = await fetch(`${apiUrl}/api/v1/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          current_password: passwordForm.currentPassword,
          new_password: passwordForm.newPassword
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to change password');
      }

      setPasswordSuccess('Password changed successfully! 🎉');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      setTimeout(() => {
        setShowChangePassword(false);
        setPasswordSuccess('');
      }, 2000);

    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError(error.message || 'Failed to change password. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  // ============================================
  // MAIN RENDER (REMAINING MODALS AND UI)
  // ============================================
  return (
    <div className="emit-minimal-container">
      {/* Header */}
      <div className="emit-header">
        <div style={{
          position: 'absolute',
          top: '12px',
          left: '12px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px'
        }}>
          <span style={{
            fontSize: '10px',
            color: '#FFD700',
            fontWeight: '600',
            fontStyle: 'italic',
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}>
            Powered by
          </span>
          <img 
            src="/fho-logo.svg" 
            alt="FHO" 
            style={{
              width: '100px',
              height: '100px',
              opacity: 0.9
            }}
          />
        </div>
        <h1>📚 EMIT Research</h1>
        <div style={{ position: 'absolute', top: '20px', right: '20px' }}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            style={{
              padding: '10px 16px',
              background: 'linear-gradient(135deg, #4A90E2, #6CB4EE)',
              border: 'none',
              borderRadius: '20px',
              color: 'white',
              fontSize: '20px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(74, 144, 226, 0.3)'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(74, 144, 226, 0.4)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(74, 144, 226, 0.3)';
            }}
          >
            ☰
          </button>
          {showMenu && (
            <div 
              ref={menuRef}
              style={{
              position: 'absolute',
              top: '0',
              right: '40px',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
              overflow: 'auto',
              minWidth: '200px',
              maxHeight: 'calc(100vh - 80px)',
              zIndex: 1000
            }}>
              <button
                onClick={() => {
                  setShowMenu(false);
                  window.location.href = '/platform';
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: 'white',
                  border: 'none',
                  borderBottom: '1px solid #f0f0f0',
                  color: '#2C3E50',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#f8f9fa'}
                onMouseOut={(e) => e.target.style.background = 'white'}
              >
                🌐 Explore FHO Platform
              </button>
              <button
                onClick={() => {
                  setShowMenu(false);
                  window.location.href = '/emit-research/setup';
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: 'white',
                  border: 'none',
                  borderBottom: '1px solid #f0f0f0',
                  color: '#2C3E50',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#f8f9fa'}
                onMouseOut={(e) => e.target.style.background = 'white'}
              >
                📝 Research Profile
              </button>
              <button
                onClick={() => {
                  setShowMenu(false);
                  window.location.href = '/emit-the-bible';
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: 'white',
                  border: 'none',
                  borderBottom: '1px solid #f0f0f0',
                  color: '#2C3E50',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#f8f9fa'}
                onMouseOut={(e) => e.target.style.background = 'white'}
              >
                ✨ Switch to Emit
              </button>
              <button
                onClick={() => {
                  setShowMenu(false);
                  setConversations([]);
                  setCurrentConversationId(null);
                  setUserInput('');
                  setResearchMode('select');
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: 'white',
                  border: 'none',
                  borderBottom: '1px solid #f0f0f0',
                  color: '#2C3E50',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#f8f9fa'}
                onMouseOut={(e) => e.target.style.background = 'white'}
              >
                🆕 New Conversation
              </button>
              <button
                onClick={() => {
                  setShowMenu(false);
                  setShowConversationHistory(true);
                  loadResearchHistory();
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: 'white',
                  border: 'none',
                  borderBottom: '1px solid #f0f0f0',
                  color: '#2C3E50',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#f8f9fa'}
                onMouseOut={(e) => e.target.style.background = 'white'}
              >
                📜 Conversation History
              </button>
              <button
                onClick={() => {
                  setShowMenu(false);
                  setShowBibleReference(true);
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: 'white',
                  border: 'none',
                  borderBottom: '1px solid #f0f0f0',
                  color: '#2C3E50',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#f8f9fa'}
                onMouseOut={(e) => e.target.style.background = 'white'}
              >
                📖 Bible Reference
              </button>
              <button
                onClick={() => {
                  setShowMenu(false);
                  setShowBiblicalTopics(true);
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: 'white',
                  border: 'none',
                  borderBottom: '1px solid #f0f0f0',
                  color: '#2C3E50',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#f8f9fa'}
                onMouseOut={(e) => e.target.style.background = 'white'}
              >
                💡 Biblical Topics
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  window.location.href = '/emit-the-bible/setup';
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: 'white',
                  border: 'none',
                  borderBottom: '1px solid #f0f0f0',
                  color: '#2C3E50',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#f8f9fa'}
                onMouseOut={(e) => e.target.style.background = 'white'}
              >
                ✏️ Edit Profile
              </button>
              <button
                onClick={() => {
                  setShowMenu(false);
                  setShowChangePassword(true);
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: 'white',
                  border: 'none',
                  borderBottom: '1px solid #f0f0f0',
                  color: '#2C3E50',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#f8f9fa'}
                onMouseOut={(e) => e.target.style.background = 'white'}
              >
                🔒 Change Password
              </button>
              <button
                onClick={() => {
                  setShowMenu(false);
                  // Clear authentication tokens
                  localStorage.removeItem('token');
                  localStorage.removeItem('user');
                  // Redirect to login page
                  window.location.href = '/login';
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: 'white',
                  border: 'none',
                  color: '#dc3545',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#fff5f5'}
                onMouseOut={(e) => e.target.style.background = 'white'}
              >
                🚪 Logout
              </button>
              <div style={{ padding: '0 12px 10px', background: 'white' }}><DeleteAccountButton variant="light" /></div>
              
              {/* FHO Apps Section - Direct */}
              <div style={{ 
                borderTop: '2px solid #e0e0e0',
                background: 'linear-gradient(135deg, #E8EEF2, #F0F4F8)',
                paddingTop: '8px'
              }}>
                <div style={{ padding: '8px 20px', fontSize: '12px', color: '#666', fontWeight: '600' }}>
                  📱 FHO APPS
                </div>
                <div style={{ background: 'transparent' }}>
                  <button
                    onClick={() => { setShowMenu(false); window.location.href = '/family-coach'; }}
                    style={{
                      width: '100%',
                      padding: '8px 16px 8px 28px',
                      background: 'transparent',
                      border: 'none',
                      color: '#5A6A7A',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.5)'}
                    onMouseOut={(e) => e.target.style.background = 'transparent'}
                  >
                    👨‍👩‍👧 Family Coach
                  </button>
                  <button
                    onClick={() => { setShowMenu(false); window.location.href = '/mental-performance'; }}
                    style={{
                      width: '100%',
                      padding: '8px 16px 8px 28px',
                      background: 'transparent',
                      border: 'none',
                      color: '#5A6A7A',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.5)'}
                    onMouseOut={(e) => e.target.style.background = 'transparent'}
                  >
                    🏆 Mental Performance
                  </button>
                  <button
                    id="safe-buddy-btn"
                    onClick={() => { setShowMenu(false); window.location.href = '/safe'; }}
                    style={{
                      width: '100%',
                      padding: '8px 16px 8px 28px',
                      background: 'transparent',
                      border: 'none',
                      color: '#5A6A7A',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.5)'}
                    onMouseOut={(e) => e.target.style.background = 'transparent'}
                  >
                    🛡️ SAFE Buddy
                  </button>
                  <button
                    onClick={() => { setShowMenu(false); window.location.href = '/journey'; }}
                    style={{
                      width: '100%',
                      padding: '8px 16px 8px 28px',
                      background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(102,126,234,0.15))',
                      border: 'none',
                      borderTop: '1px solid rgba(16,185,129,0.3)',
                      color: '#059669',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseOver={(e) => e.target.style.background = 'rgba(16,185,129,0.25)'}
                    onMouseOut={(e) => e.target.style.background = 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(102,126,234,0.15))'}
                  >
                    ✨ My Journey
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="subtitle">Original academic papers, expertly crafted</div>
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '200px' }}>
        {activeTab === 'progress' && (
          <ProgressTab
            loading={loading}
            error={error}
            userData={userData}
            onRetry={fetchDashboardData}
            onPracticeClick={handlePracticeClick}
            getMindStreamIcon={getMindStreamIcon}
            getMindStreamColor={getMindStreamColor}
          />
        )}
        {activeTab === 'analytics' && (
          <AnalyticsTab
            loading={loading}
            userData={userData}
            getLevelInfo={getLevelInfo}
            getNextLevelUnlock={getNextLevelUnlock}
            getMindStreamColor={getMindStreamColor}
          />
        )}

        {activeTab === 'chat' && researchMode === 'select' && (
          <div style={{
            position:'fixed', inset:0, zIndex:9999,
            background:'linear-gradient(135deg,#0f172a 0%,#1e1b4b 100%)',
            display:'flex', justifyContent:'center',
            overflowY:'auto', padding:'0',
          }}>
            <div style={{
              maxWidth:'520px', width:'100%', textAlign:'center',
              margin:'auto', padding:'32px 24px',
              minHeight:'min-content',
            }}>
              {/* Header */}
              <div style={{ marginBottom:'16px' }}>
                <div style={{ fontSize:'2.2rem', marginBottom:'8px' }}>📚</div>
                <h1 style={{ color:'#fff', fontSize:'1.5rem', fontWeight:700, margin:'0 0 8px 0' }}>
                  EMIT Research
                </h1>
                <p style={{ color:'#94a3b8', fontSize:'0.9rem', margin:0 }}>
                  What would you like to do today?
                </p>
              </div>

              {/* Mode cards */}
              <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginBottom:'16px' }}>

                {/* Research Mode */}
                <button
                  onClick={() => setResearchMode('research')}
                  style={{
                    background:'rgba(79,70,229,0.15)',
                    border:'1px solid rgba(79,70,229,0.5)',
                    borderRadius:'14px', padding:'14px 18px',
                    cursor:'pointer', textAlign:'left',
                    transition:'all 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(79,70,229,0.28)'}
                  onMouseLeave={e => e.currentTarget.style.background='rgba(79,70,229,0.15)'}
                >
                  <div style={{ display:'flex', alignItems:'flex-start', gap:'14px' }}>
                    <div style={{
                      background:'rgba(79,70,229,0.3)', borderRadius:'10px',
                      width:'44px', height:'44px', display:'flex',
                      alignItems:'center', justifyContent:'center',
                      fontSize:'1.3rem', flexShrink:0,
                    }}>🔍</div>
                    <div>
                      <div style={{ color:'#a5b4fc', fontWeight:700, fontSize:'1rem', marginBottom:'5px' }}>
                        Research a Topic
                      </div>
                      <div style={{ color:'#94a3b8', fontSize:'0.83rem', lineHeight:1.5 }}>
                        Explore ideas, analyze sources, get literature overviews, build arguments,
                        and ask scholarly questions — no paper required.
                      </div>
                    </div>
                  </div>
                </button>

                {/* Paper Writing Mode */}
                <button
                  onClick={() => setResearchMode('paper')}
                  style={{
                    background:'rgba(16,185,129,0.12)',
                    border:'1px solid rgba(16,185,129,0.4)',
                    borderRadius:'14px', padding:'14px 18px',
                    cursor:'pointer', textAlign:'left',
                    transition:'all 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(16,185,129,0.24)'}
                  onMouseLeave={e => e.currentTarget.style.background='rgba(16,185,129,0.12)'}
                >
                  <div style={{ display:'flex', alignItems:'flex-start', gap:'14px' }}>
                    <div style={{
                      background:'rgba(16,185,129,0.25)', borderRadius:'10px',
                      width:'44px', height:'44px', display:'flex',
                      alignItems:'center', justifyContent:'center',
                      fontSize:'1.3rem', flexShrink:0,
                    }}>✍️</div>
                    <div>
                      <div style={{ color:'#6ee7b7', fontWeight:700, fontSize:'1rem', marginBottom:'5px' }}>
                        Write a Paper
                      </div>
                      <div style={{ color:'#94a3b8', fontSize:'0.83rem', lineHeight:1.5 }}>
                        Paste your assignment and EMIT will interview you, gather your
                        personal insights, and produce original academic work.
                      </div>
                    </div>
                  </div>
                </button>

                {/* Sermon Mode */}
                <button
                  onClick={() => setResearchMode('sermon')}
                  style={{
                    background:'rgba(245,158,11,0.12)',
                    border:'1px solid rgba(245,158,11,0.45)',
                    borderRadius:'14px', padding:'14px 18px',
                    cursor:'pointer', textAlign:'left',
                    transition:'all 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(245,158,11,0.24)'}
                  onMouseLeave={e => e.currentTarget.style.background='rgba(245,158,11,0.12)'}
                >
                  <div style={{ display:'flex', alignItems:'flex-start', gap:'14px' }}>
                    <div style={{
                      background:'rgba(245,158,11,0.25)', borderRadius:'10px',
                      width:'44px', height:'44px', display:'flex',
                      alignItems:'center', justifyContent:'center',
                      fontSize:'1.3rem', flexShrink:0,
                    }}>{'\uD83D\uDCD6'}</div>
                    <div>
                      <div style={{ color:'#fcd34d', fontWeight:700, fontSize:'1rem', marginBottom:'5px' }}>
                        Build a Sermon
                      </div>
                      <div style={{ color:'#94a3b8', fontSize:'0.83rem', lineHeight:1.5 }}>
                        Sermons, short Bible studies, and devotionals for church, a team or
                        corporate setting, an airport chapel, or a hospital.
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              <p style={{ color:'#475569', fontSize:'0.78rem' }}>
                You can switch modes anytime using the menu above.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'chat' && (
          <>
          {/* Mode indicator */}
          {researchMode !== 'select' && (
            <div style={{
              display:'flex', alignItems:'center', gap:'8px',
              padding:'6px 12px',
              background: researchMode === 'research' ? 'rgba(79,70,229,0.15)' : researchMode === 'sermon' ? 'rgba(245,158,11,0.12)' : 'rgba(16,185,129,0.12)',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}>
              <span style={{ fontSize:'0.8rem' }}>{researchMode === 'research' ? '🔍' : researchMode === 'sermon' ? '📖' : '✍️'}</span>
              <span style={{
                color: researchMode === 'research' ? '#a5b4fc' : researchMode === 'sermon' ? '#fcd34d' : '#6ee7b7',
                fontSize:'0.78rem', fontWeight:600,
              }}>
                {researchMode === 'research' ? 'Research Mode' : researchMode === 'sermon' ? 'Sermon Mode' : 'Paper Writing Mode'}
              </span>
              <button
                onClick={() => { setResearchMode('select'); }}
                style={{
                  marginLeft:'auto',
                  background:'#1e3a5f',
                  border:'2px solid #ffffff',
                  color:'#ffffff', borderRadius:'8px',
                  padding:'7px 20px', cursor:'pointer', fontSize:'0.9rem',
                  fontWeight:800, letterSpacing:'0.03em',
                  boxShadow:'0 2px 8px rgba(0,0,0,0.4)',
                }}
              >
                ⇄ Switch Mode
              </button>
            </div>
          )}
          {/* URL detected indicator */}
          {researchMode === 'research' && detectedUrl && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '6px 14px',
              background: 'rgba(79,70,229,0.12)',
              borderBottom: '1px solid rgba(79,70,229,0.2)',
              fontSize: '0.78rem',
            }}>
              <span>🔗</span>
              <span style={{ color: '#a5b4fc', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Will analyze: {detectedUrl}
              </span>
              <button
                onClick={() => { setUserInput(userInput.replace(detectedUrl, '').trim()); setDetectedUrl(''); }}
                style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.8rem' }}
              >✕</button>
            </div>
          )}
          {/* Uploaded document indicator */}
          {uploadedFileName ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 14px', background: 'rgba(5,150,105,0.12)', borderBottom: '1px solid rgba(5,150,105,0.2)', fontSize: '0.78rem' }}>
              <span>📄</span>
              <span style={{ color: '#ffffff', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Citing from: {uploadedFileName}</span>
              <button onClick={() => { setUploadedDoc(null); setUploadedFileName(''); }} style={{ background: 'transparent', border: 'none', color: '#ffffff', cursor: 'pointer', fontSize: '0.8rem' }}>✕</button>
            </div>
          ) : null}
          {researchMode === 'sermon' && !((conversations.find(c => c.id === currentConversationId) || {}).messages || []).length && (
            <SermonBuilder onBuild={(p) => handleSubmit(p)} disabled={isProcessing} />
          )}
          <VoiceChoiceModal isOpen={showVoicePanel} theme={{}} voices={voiceList} choice={voiceChoice}
            onClose={() => { stopCoachVoice(); try { window.speechSynthesis.cancel(); } catch (e) { /* no speech */ } setShowVoicePanel(false); }}
            onChoose={(id) => { setVoiceChoice(id); saveCoachVoice(id); }}
            onPreview={previewCoachVoice} voiceOn={voice.voiceEnabled} onToggleVoice={() => voice.toggleVoice()} />
          <ChatTab
            enableListen
            onOpenVoice={() => setShowVoicePanel(true)}
            userName={userName}
            conversations={conversations}
            currentConversationId={currentConversationId}
            chatError={chatError}
            userInput={userInput}
            setUserInput={setUserInput}
            handleSubmit={handleSubmit}
            handleKeyPress={handleKeyPress}
            isProcessing={isProcessing}
            voice={voice}
            setShowShareModal={setShowShareModal}
            setShareMessage={setShareMessage}
            getMindStreamColor={getMindStreamColor}
            fileInputRef={fileInputRef}
            handleFileUpload={handleFileUpload}
            isUploading={isUploading}
            onDownloadReply={downloadReply}
          />
          </>
        )}
        {activeTab === 'practice' && (
          <PracticeTab
            practiceForm={practiceForm}
            setPracticeForm={setPracticeForm}
            practiceError={practiceError}
            practiceSuccess={practiceSuccess}
            isLoggingPractice={isSubmittingPractice}
            onSubmit={handlePracticeSubmit}
            categories={categories}
          />
        )}
      </div>

      {/* Social Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        shareMessage={shareMessage}
      />
      {/* Practice Detail Modal */}
      <PracticeDetailModal
        isOpen={showPracticeModal}
        onClose={() => setShowPracticeModal(false)}
        selectedPractice={selectedPractice}
        getMindStreamColor={getMindStreamColor}
      />

      {/* Bible Reference Modal */}
      <BibleReferenceModal
        isOpen={showBibleReference}
        onClose={() => setShowBibleReference(false)}
      />
      {/* User Guide Modal */}
      <UserGuideModal
        isOpen={showUserGuide}
        onClose={() => setShowUserGuide(false)}
      />
      {showConversationHistory && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>Research Conversations</h2>
              <button
                onClick={() => setShowConversationHistory(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}
              >x</button>
            </div>
            <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
              {loadingHistory ? (
                <p style={{ color: '#6b7280', textAlign: 'center' }}>Loading...</p>
              ) : researchConversations.length === 0 ? (
                <p style={{ color: '#6b7280', textAlign: 'center' }}>No conversations yet</p>
              ) : (
                researchConversations.map((conv, idx) => (
                  <div
                    key={conv.id || idx}
                    onClick={() => loadConversationMessages(conv.id)}
                    style={{
                      padding: '16px',
                      marginBottom: '12px',
                      borderRadius: '12px',
                      background: currentConversationId === conv.id ? '#dbeafe' : '#f3f4f6',
                      cursor: 'pointer',
                      border: currentConversationId === conv.id ? '2px solid #3b82f6' : '2px solid transparent'
                    }}
                  >
                    <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                      {conv.preview ? conv.preview.substring(0, 60) + '...' : 'Conversation'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {conv.message_count || 0} messages
                    </div>
                      <ConversationActions app="research" canExport conversationId={conv.id} onDeleted={(id) => { setResearchConversations(prev => prev.filter(c => c.id !== id)); if (currentConversationId === id) { setCurrentConversationId(null); setConversations([{ id: Date.now().toString(), messages: [] }]); } }} />
                  </div>
                ))
              )}
            </div>
            <div style={{ padding: '16px', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
              <button
                onClick={() => { setCurrentConversationId(null); setShowConversationHistory(false); }}
                style={{ padding: '12px 24px', background: '#10B981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}
              >+ Start New Conversation</button>
            </div>
          </div>
        </div>
      )}

      <BiblicalTopicsModal
        isOpen={showBiblicalTopics}
        onClose={() => setShowBiblicalTopics(false)}
        onSelectTopic={handleTopicSelect}
      />
      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        passwordForm={passwordForm}
        setPasswordForm={setPasswordForm}
        passwordError={passwordError}
        passwordSuccess={passwordSuccess}
        isChangingPassword={isChangingPassword}
        onSubmit={handleChangePassword}
      />
      {/* Bottom Tab Navigation */}
      <div className="emit-tabs">
        <button
          className={`emit-tab ${activeTab === 'progress' ? 'active' : ''}`}
          onClick={() => setActiveTab('progress')}
        >
          <Target size={24} />
          <span>Progress</span>
        </button>

        <button
          className={`emit-tab ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          <BarChart3 size={24} />
          <span>Analytics</span>
        </button>

        <button
          className={`emit-tab ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => setActiveTab('chat')}
        >
          <MessageCircle size={24} />
          <span>Chat</span>
        </button>

        <button
          className={`emit-tab ${activeTab === 'practice' ? 'active' : ''}`}
          onClick={() => setActiveTab('practice')}
        >
          <BookOpen size={24} />
          <span>Log Practice</span>
        </button>
      </div>

      {/* Response Counter - Only for free users */}
      {subscriptionStatus === 'free' && responsesRemaining !== null && (
        <div style={{ 
          position: 'fixed', 
          top: '80px', 
          right: '20px', 
          zIndex: 40 
        }}>
          <ResponseCounter responsesRemaining={responsesRemaining} />
        </div>
      )}

      {/* Paywall Modal */}
      {showPaywall && (
        <PaywallModal onClose={() => setShowPaywall(false)} />
      )}
    </div>
  );
};

export default MinimalDashboard;
